/**
 * Utils, but importable via node (no transitive imports to things that will fail to resolve when run in a node context)
 * This is `export *`'d from utils for use on frontend, but this file itself can be used in tests or etc.
 */

/**
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
 *
 * escape regex metachars to support compiling a user-provided string as a regex
 */
export function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

/**
 * shorthand support for the common pattern of `new RegExp(escapeRegExp(pattern), flags)`
 */
export function EscapedRegExp(pattern: string, flags?: string) {
  return new RegExp(escapeRegExp(pattern), flags);
}

/**
 * build a new string that is identical to the old string but with ascii-like characters transformed to their ascii counterpart.
 * This is almost certainly not totally correct, but should be GoodEnough™.
 */
export function diacritishToAscii(str: string) {
  const map : Record<string, string> = {
    // Lowercase
    à: 'a', á: 'a', â: 'a', ã: 'a', ä: 'a', å: 'a', ā: 'a', ă: 'a', ą: 'a',
    ç: 'c', ć: 'c', ĉ: 'c', č: 'c', ċ: 'c',
    ď: 'd', đ: 'd', ð: 'd',
    è: 'e', é: 'e', ê: 'e', ë: 'e', ē: 'e', ė: 'e', ę: 'e', ě: 'e',
    ƒ: 'f',
    ĝ: 'g', ğ: 'g', ġ: 'g', ģ: 'g',
    ĥ: 'h', ħ: 'h',
    ì: 'i', í: 'i', î: 'i', ï: 'i', ī: 'i', į: 'i', ı: 'i',
    ĳ: 'ij',
    ĵ: 'j',
    ķ: 'k',
    ĺ: 'l', ļ: 'l', ľ: 'l', ŀ: 'l', ł: 'l',
    ñ: 'n', ń: 'n', ņ: 'n', ň: 'n', ŋ: 'n',
    ò: 'o', ó: 'o', ô: 'o', õ: 'o', ö: 'o', ø: 'o', ō: 'o', ő: 'o',
    œ: 'oe',
    ŕ: 'r', ŗ: 'r', ř: 'r',
    ś: 's', ŝ: 's', ş: 's', š: 's', ß: 'ss',
    ţ: 't', ť: 't', ŧ: 't',
    ù: 'u', ú: 'u', û: 'u', ü: 'u', ū: 'u', ů: 'u', ű: 'u', ų: 'u',
    ŵ: 'w',
    ý: 'y', ÿ: 'y', ŷ: 'y',
    ź: 'z', ż: 'z', ž: 'z',

    // Uppercase
    À: 'A', Á: 'A', Â: 'A', Ã: 'A', Ä: 'A', Å: 'A', Ā: 'A', Ă: 'A', Ą: 'A',
    Ç: 'C', Ć: 'C', Ĉ: 'C', Č: 'C', Ċ: 'C',
    Ď: 'D', Đ: 'D',
    È: 'E', É: 'E', Ê: 'E', Ë: 'E', Ē: 'E', Ė: 'E', Ę: 'E', Ě: 'E',
    Ĝ: 'G', Ğ: 'G', Ġ: 'G', Ģ: 'G',
    Ĥ: 'H', Ħ: 'H',
    Ì: 'I', Í: 'I', Î: 'I', Ï: 'I', Ī: 'I', Į: 'I',
    Ĵ: 'J',
    Ķ: 'K',
    Ĺ: 'L', Ļ: 'L', Ľ: 'L', Ŀ: 'L', Ł: 'L',
    Ñ: 'N', Ń: 'N', Ņ: 'N', Ň: 'N', Ŋ: 'N',
    Ò: 'O', Ó: 'O', Ô: 'O', Õ: 'O', Ö: 'O', Ø: 'O', Ō: 'O', Ő: 'O',
    Œ: 'OE',
    Ŕ: 'R', Ŗ: 'R', Ř: 'R',
    Ś: 'S', Ŝ: 'S', Ş: 'S', Š: 'S',
    Ţ: 'T', Ť: 'T', Ŧ: 'T',
    Ù: 'U', Ú: 'U', Û: 'U', Ü: 'U', Ū: 'U', Ů: 'U', Ű: 'U', Ų: 'U',
    Ŵ: 'W',
    Ý: 'Y', Ŷ: 'Y', Ÿ: 'Y',
    Ź: 'Z', Ż: 'Z', Ž: 'Z',
  };

  return str.replace(
    /[^ -~]/gs, // everything that is NOT an printable ascii character
    ch => map[ch] || ch
  );
}
